import styled, { css } from 'styled-components';

// Colours from the approved design (School Plan Request Form canvas, 2026-10-20).
const ink = '#1D1541';
const text = '#1E252A';
const secondary = '#4A4458';
const muted = '#6B6577';
const accent = '#5B12C9';
const primary = '#2E2277';
const border = '#DED7EC';
const softBorder = '#E5DFEF';
const error = '#A51D38';
const phone = '640px';

// Above the mobile shell (5000) and the mobile upgrade layer (6000), below toasts (9999).
export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 6100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(37, 34, 42, 0.45);
  font-family: var(
    --font-family-ibm_plex_sans,
    'IBM Plex Sans',
    Helvetica,
    sans-serif
  );
  color: ${text};

  @media (max-width: ${phone}) {
    padding: 0;
    align-items: stretch;
  }
`;

export const Dialog = styled.div`
  position: relative;
  width: min(940px, 100%);
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px);
  overflow-y: auto;
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 24px 64px rgba(46, 34, 119, 0.18);
  outline: none;

  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
  }

  @media (max-width: ${phone}) {
    width: 100%;
    max-height: none;
    height: 100%;
    border-radius: 0;
  }

  & *:focus-visible {
    outline: 3px solid #8e65e2;
    outline-offset: 2px;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 1;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: ${secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #f3effa;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

export const Benefits = styled.aside`
  background: #f5f2ff;
  padding: 40px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 28px;

  @media (max-width: 860px) {
    display: none;
  }
`;

export const Eyebrow = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${accent};
`;

export const BenefitsTitle = styled.p`
  margin: 14px 0 0;
  font-size: 28px;
  line-height: 1.2;
  font-weight: 600;
  color: ${ink};
  letter-spacing: -0.4px;
`;

export const BenefitList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;

  li {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    font-size: 15px;
    line-height: 1.45;
    font-weight: 500;
    color: ${ink};
  }

  li::before {
    content: none;
  }
`;

export const CheckBadge = styled.span`
  flex: 0 0 26px;
  height: 26px;
  border-radius: 50%;
  background: ${(props) => (props.$dark ? primary : '#e4d7fd')};
  color: ${(props) => (props.$dark ? '#fff' : accent)};
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const IncludesLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${secondary};
  margin-bottom: 10px;
`;

export const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  span {
    padding: 5px 10px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid #e2d9f3;
    font-size: 12px;
    color: ${primary};
  }
`;

export const TalkBlock = styled.div`
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid #e2d9f3;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${ink};

  a {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    font-size: 14px;
    font-weight: 500;
    color: ${accent};
    text-decoration: none;
  }

  a:hover {
    color: ${primary};
    text-decoration: underline;
  }
`;

export const Main = styled.div`
  position: relative;
  min-width: 0;
`;

export const Form = styled.form`
  padding: 36px 40px 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: ${phone}) {
    padding: 20px 20px 0;
    gap: 16px;
  }
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 40px;
`;

export const SentPill = styled.span`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px 5px 8px;
  border-radius: 999px;
  background: #e6f3ef;
  border: 1px solid #96cebb;
  color: #005f3f;
  font-size: 12px;
  font-weight: 600;
`;

export const Title = styled.h2`
  margin: 4px 0 0;
  font-size: 26px;
  line-height: 1.2;
  font-weight: 600;
  color: ${ink};
  letter-spacing: -0.3px;

  @media (max-width: ${phone}) {
    font-size: 24px;
  }
`;

export const Lead = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: ${secondary};
`;

export const ContactCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 6px 10px 14px;
  border: 1px solid ${softBorder};
  border-radius: 12px;
  background: #fcfafe;
  min-height: 60px;
  box-sizing: border-box;
`;

export const Avatar = styled.div`
  flex: 0 0 38px;
  height: 38px;
  border-radius: 50%;
  background: ${primary};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
`;

export const ContactText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;

  strong {
    font-size: 14px;
    font-weight: 600;
    color: ${ink};
  }

  span {
    font-size: 13px;
    color: ${secondary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const TextButton = styled.button`
  height: 44px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${accent};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f3effa;
  }
`;

export const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: ${phone}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
  grid-column: ${(props) => (props.$wide ? '1 / -1' : 'auto')};
`;

export const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${ink};
`;

export const Legend = styled.legend`
  padding: 0;
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${ink};
`;

export const Required = styled.span`
  color: ${accent};
`;

export const Hint = styled.span`
  margin-left: 8px;
  font-size: 12px;
  font-weight: 400;
  color: ${muted};
`;

export const Optional = styled.span`
  font-weight: 400;
  color: ${muted};
`;

const control = css`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${(props) => (props['aria-invalid'] ? error : border)};
  border-radius: 8px;
  background: #fff;
  color: ${text};
  font: inherit;
  font-size: 15px;

  &::placeholder {
    color: #7a7486;
    opacity: 1;
  }

  &:focus {
    outline: none;
    border-color: #7200e0;
    box-shadow: 0 0 0 3px rgba(114, 0, 224, 0.16);
  }

  @media (max-width: ${phone}) {
    font-size: 16px;
  }
`;

export const Input = styled.input`
  ${control};
  height: 47px;
  padding: 0 13px;
`;

export const SelectWrap = styled.div`
  position: relative;

  svg {
    position: absolute;
    right: 14px;
    top: 20px;
    pointer-events: none;
  }
`;

export const Select = styled.select`
  ${control};
  height: 47px;
  padding: 0 38px 0 13px;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  color: ${(props) => (props.value ? text : '#7a7486')};

  option {
    color: ${text};
  }
`;

export const TextArea = styled.textarea`
  ${control};
  min-height: 70px;
  padding: 11px 13px;
  line-height: 1.5;
  resize: vertical;
`;

export const Fieldset = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
`;

export const ChipGroup = styled.div`
  display: ${(props) => (props.$grid ? 'grid' : 'flex')};
  grid-template-columns: repeat(4, minmax(0, 1fr));
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: ${phone}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const Chip = styled.button`
  height: 44px;
  padding: 0 16px;
  border-radius: ${(props) => (props.$pill ? '999px' : '10px')};
  border: 1.5px solid
    ${(props) =>
      props['aria-pressed'] ? primary : props.$invalid ? error : border};
  background: ${(props) => (props['aria-pressed'] ? '#f0e7ff' : '#fff')};
  color: ${ink};
  font: inherit;
  font-size: 14px;
  font-weight: ${(props) => (props['aria-pressed'] ? 600 : 500)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;

  &:hover {
    border-color: ${primary};
  }
`;

export const ErrorText = styled.div`
  margin-top: ${(props) => (props.$spaced ? '7px' : 0)};
  font-size: 13px;
  color: ${error};
`;

export const SubmitArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;

  @media (max-width: ${phone}) {
    position: sticky;
    bottom: 0;
    margin: 0 -20px;
    padding: 12px 20px 22px;
    background: #fff;
    border-top: 1px solid #eee9f5;
    box-shadow: 0 -8px 24px rgba(46, 34, 119, 0.06);
  }
`;

export const PrimaryButton = styled.button`
  height: 50px;
  border: 0;
  border-radius: 10px;
  background: ${primary};
  color: #fff;
  font: inherit;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  flex: ${(props) => (props.$grow ? '1 1 180px' : 'none')};

  &:hover:not(:disabled) {
    background: #3d2d99;
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
`;

export const SecondaryLink = styled.a`
  flex: 1 1 180px;
  height: 50px;
  box-sizing: border-box;
  border: 1.5px solid ${primary};
  border-radius: 10px;
  background: #fff;
  color: ${primary};
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;

  &:hover {
    background: #f8f6ff;
  }
`;

export const FinePrint = styled.p`
  margin: 0;
  text-align: center;
  font-size: 12px;
  line-height: 1.5;
  color: ${muted};

  a {
    color: ${accent};
  }

  &[data-phone-only] {
    display: none;
  }

  @media (max-width: 860px) {
    &[data-phone-only] {
      display: block;
    }
  }
`;

export const FormError = styled.div`
  padding: 12px 14px;
  border: 1px solid ${error};
  border-radius: 8px;
  color: ${error};
  font-size: 14px;
  line-height: 1.45;
`;

export const Sent = styled.div`
  padding: 56px 44px 36px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: ${phone}) {
    padding: 40px 20px 24px;
  }
`;

export const SuccessIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #e6f3ef;
  color: #005f3f;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const NextSteps = styled.div`
  padding: 20px 22px;
  border: 1px solid ${softBorder};
  border-radius: 14px;
  background: #fcfafe;
  display: flex;
  flex-direction: column;
  gap: 14px;

  ol {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  li {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    font-size: 15px;
    line-height: 1.45;
    color: ${ink};
  }
`;

export const NextStepsLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${accent};
`;

export const Actions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;
